import * as angular from 'angular';

import { ServiceStatus } from '../../models/enum';
import { IJobDefinitionService } from './';
import { IRequestService } from '../../services';
import { Group, GroupSchedulingViewModelResponse, KeyValuePair, OnlineLog, OnlineLogViewModel } from '../../models';
import { Dictionary } from 'lodash';

export interface IOnlineComputationService {
    start(job): angular.IPromise<GroupSchedulingViewModelResponse>;
    startAtLastProcess(job): angular.IPromise<GroupSchedulingViewModelResponse>;
    /**
     * Stops an online job.
     * @param {string} job The guid of the job to stop.
     * @returns {Promise} The list of dates that were canceled.
     */
    stop(job): angular.IPromise<string[]>;
    status(): angular.IPromise<ServiceStatus>;
    getSchedulingViewModels(): angular.IPromise<GroupSchedulingViewModelResponse>;
    getSchedulingViewModels(cancel: boolean): angular.IPromise<GroupSchedulingViewModelResponse>;
    getElapsedPrevious(): angular.IPromise<Dictionary<Dictionary<number>>>;
    getElapsedPrevious(cancel: boolean): angular.IPromise<Dictionary<Dictionary<number>>>;
    getElapsedAcquisition(): angular.IPromise<KeyValuePair<string, number>[]>;
    getElapsedAcquisition(cancel: boolean): angular.IPromise<KeyValuePair<string, number>[]>;
    getOnlineErrors(beginDateTime: string, endDateTime: string, force: boolean): angular.IPromise<OnlineLog[]>;
}

export class OnlineComputationService implements IOnlineComputationService {
    static serviceId = 'onlineComputationService';
    private _controllerUrl: string;

    static $inject = [
        '$q',
        'jobDefinitionService',
        'requestService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _jobDefinitionService: IJobDefinitionService,
        private _requestService: IRequestService
    ) {
    }

    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + 'onlineComputation/';
        }
        return this._controllerUrl;
    }

    start(job): angular.IPromise<GroupSchedulingViewModelResponse> {
        const url = this.controllerUrl() + 'start/';
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'jobDefinition/');
        return this._requestService.post(url, job);
    }

    startAtLastProcess(job): angular.IPromise<GroupSchedulingViewModelResponse> {
        const defered = this._$q.defer<GroupSchedulingViewModelResponse>();
        job.startDate = job.lastProcessed;

        this._jobDefinitionService.update(job)
            .then(() => {
                return this.start(job);
            })
            .then((data) => {
                defered.resolve(data);
            })
            .catch((reason) => {
                defered.reject(reason);
            });

        return defered.promise;
    }

    stop(job): angular.IPromise<any> {
        const url = this.controllerUrl() + 'stop/';
        this._requestService.cleanCache(this._requestService.getUrlV1() + 'jobDefinition/');
        return this._requestService.post(url, { guid: job.guid, groupGuid: job.groupGuid });
    }

    status(): angular.IPromise<ServiceStatus> {
        const url = this.controllerUrl() + 'status/';
        this._requestService.cleanCache(url);
        return this._requestService.get(url);
    }

    getSchedulingViewModels(): angular.IPromise<GroupSchedulingViewModelResponse>;
    getSchedulingViewModels(cancel: boolean): angular.IPromise<GroupSchedulingViewModelResponse>;
    getSchedulingViewModels(cancel?: boolean): angular.IPromise<GroupSchedulingViewModelResponse> {
        this._requestService.cleanCacheAtV1('groupScheduling/');
        return this._requestService.getAtV1<GroupSchedulingViewModelResponse>('groupScheduling/', null, true);
    }

    getElapsedPrevious(): angular.IPromise<Dictionary<Dictionary<number>>>;
    getElapsedPrevious(cancel: boolean): angular.IPromise<Dictionary<Dictionary<number>>>;
    getElapsedPrevious(cancel?: boolean): angular.IPromise<Dictionary<Dictionary<number>>> {
        this._requestService.cleanCacheAtV1('groupScheduling/elapsedPrevious');
        return this._requestService.getAtV1<Dictionary<Dictionary<number>>>('groupScheduling/elapsedPrevious', null, true);
    }

    getElapsedAcquisition(): angular.IPromise<KeyValuePair<string, number>[]>;
    getElapsedAcquisition(cancel: boolean): angular.IPromise<KeyValuePair<string, number>[]>;
    getElapsedAcquisition(cancel?: boolean): angular.IPromise<KeyValuePair<string, number>[]> {
        this._requestService.cleanCacheAtV1('groupScheduling/elapsedAcquisition');
        return this._requestService.getAtV1<KeyValuePair<string, number>[]>('groupScheduling/elapsedAcquisition', null, true);
    }

    getOnlineErrors(beginDateTime: string, endDateTime: string): angular.IPromise<OnlineLog[]>;
    getOnlineErrors(beginDateTime: string, endDateTime: string, force: boolean): angular.IPromise<OnlineLog[]>;
    getOnlineErrors(beginDateTime: string, endDateTime: string, force?: boolean): angular.IPromise<OnlineLog[]> {
        const url = `${this.controllerUrl()}getOnlineErrors/`;
        const params = { beginDateTime, endDateTime };
        if (force) {
            this._requestService.cleanCacheAtV1(url);
        }
        return this._requestService
            .post(url, params);
    }
}